import { DecimalPipe } from '@angular/common';
import { Component, computed, input } from '@angular/core';

export interface PortfolioCompositionChartSlice {
	name: string;
	value: number;
	color: string;
	percentage: number;
}

interface RenderedSlice extends PortfolioCompositionChartSlice {
	path: string;
	labelX: number;
	labelY: number;
	showLabel: boolean;
	isFullCircle: boolean;
}

const OUTER_RADIUS = 80;
const INNER_RADIUS = OUTER_RADIUS * 0.4;
const LABEL_RADIUS = (OUTER_RADIUS + INNER_RADIUS) / 2;
const SLICE_GAP_RADIANS = 3.5 / OUTER_RADIUS;
const SLICE_CORNER_RADIUS = 3;
const LABEL_CHARACTER_WIDTH = 9.5;

function pointAt(radius: number, angle: number): [number, number] {
	return [radius * Math.cos(angle), radius * Math.sin(angle)];
}

function cornerGeometry(radius: number, cornerRadius: number, outer: boolean) {
	const sine = outer ? cornerRadius / (radius - cornerRadius) : cornerRadius / (radius + cornerRadius);
	const tangentAngle = Math.asin(Math.min(sine, 1));
	const radialDistance = outer
		? (radius - cornerRadius) * Math.cos(tangentAngle)
		: (radius + cornerRadius) * Math.cos(tangentAngle);

	return {
		tangentAngle,
		radialDistance
	};
}

function roundedSlicePath(startAngle: number, endAngle: number): string {
	const angle = endAngle - startAngle;
	const largeArc = angle > Math.PI ? 1 : 0;
	const maxTangentAngle = angle / 4;
	const maxCornerRadius = Math.min(
		(OUTER_RADIUS * Math.sin(maxTangentAngle)) / (1 + Math.sin(maxTangentAngle)),
		(INNER_RADIUS * Math.sin(maxTangentAngle)) / (1 - Math.sin(maxTangentAngle))
	);
	const cornerRadius = Math.min(SLICE_CORNER_RADIUS, maxCornerRadius);
	const outer = cornerGeometry(OUTER_RADIUS, cornerRadius, true);
	const inner = cornerGeometry(INNER_RADIUS, cornerRadius, false);
	const outerStart = pointAt(OUTER_RADIUS, startAngle + outer.tangentAngle);
	const outerEnd = pointAt(OUTER_RADIUS, endAngle - outer.tangentAngle);
	const outerEndRadial = pointAt(outer.radialDistance, endAngle);
	const innerEndRadial = pointAt(inner.radialDistance, endAngle);
	const innerEnd = pointAt(INNER_RADIUS, endAngle - inner.tangentAngle);
	const innerStart = pointAt(INNER_RADIUS, startAngle + inner.tangentAngle);
	const innerStartRadial = pointAt(inner.radialDistance, startAngle);
	const outerStartRadial = pointAt(outer.radialDistance, startAngle);

	return [
		`M ${outerStart[0]} ${outerStart[1]}`,
		`A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArc} 1 ${outerEnd[0]} ${outerEnd[1]}`,
		`A ${cornerRadius} ${cornerRadius} 0 0 1 ${outerEndRadial[0]} ${outerEndRadial[1]}`,
		`L ${innerEndRadial[0]} ${innerEndRadial[1]}`,
		`A ${cornerRadius} ${cornerRadius} 0 0 1 ${innerEnd[0]} ${innerEnd[1]}`,
		`A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArc} 0 ${innerStart[0]} ${innerStart[1]}`,
		`A ${cornerRadius} ${cornerRadius} 0 0 1 ${innerStartRadial[0]} ${innerStartRadial[1]}`,
		`L ${outerStartRadial[0]} ${outerStartRadial[1]}`,
		`A ${cornerRadius} ${cornerRadius} 0 0 1 ${outerStart[0]} ${outerStart[1]}`,
		'Z'
	].join(' ');
}

function fullCirclePath(): string {
	const outerTop = pointAt(OUTER_RADIUS, -Math.PI / 2);
	const outerBottom = pointAt(OUTER_RADIUS, Math.PI / 2);
	const innerTop = pointAt(INNER_RADIUS, -Math.PI / 2);
	const innerBottom = pointAt(INNER_RADIUS, Math.PI / 2);

	return [
		`M ${outerTop[0]} ${outerTop[1]}`,
		`A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 0 1 ${outerBottom[0]} ${outerBottom[1]}`,
		`A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 0 1 ${outerTop[0]} ${outerTop[1]}`,
		`L ${innerTop[0]} ${innerTop[1]}`,
		`A ${INNER_RADIUS} ${INNER_RADIUS} 0 0 0 ${innerBottom[0]} ${innerBottom[1]}`,
		`A ${INNER_RADIUS} ${INNER_RADIUS} 0 0 0 ${innerTop[0]} ${innerTop[1]}`,
		'Z'
	].join(' ');
}

@Component({
	selector: 'app-portfolio-composition-chart',
	imports: [DecimalPipe],
	templateUrl: './portfolio-composition-chart.component.html'
})
export class PortfolioCompositionChartComponent {
	slices = input.required<PortfolioCompositionChartSlice[]>();
	ariaLabel = input.required<string>();

	readonly leadingSlice = computed<PortfolioCompositionChartSlice | null>(() => {
		const validSlices = this.slices().filter(slice => Number.isFinite(slice.percentage) && slice.percentage > 0);
		if (validSlices.length === 0) {
			return null;
		}

		return validSlices.reduce((largest, current) => (current.percentage > largest.percentage ? current : largest));
	});

	readonly renderedSlices = computed<RenderedSlice[]>(() => {
		const validSlices = this.slices().filter(slice => Number.isFinite(slice.percentage) && slice.percentage > 0);
		const total = validSlices.reduce((sum, slice) => sum + slice.percentage, 0);
		if (total <= 0) {
			return [];
		}

		let angle = -Math.PI / 2;
		return validSlices.map(slice => {
			const sliceAngle = (slice.percentage / total) * Math.PI * 2;
			const isFullCircle = validSlices.length === 1;
			const gap = isFullCircle ? 0 : Math.min(SLICE_GAP_RADIANS, sliceAngle / 4);
			const startAngle = angle + gap / 2;
			const endAngle = angle + sliceAngle - gap / 2;
			const midpoint = angle + sliceAngle / 2;
			const [labelX, labelY] = pointAt(LABEL_RADIUS, midpoint);
			const labelLength = `${Math.round(slice.percentage)}%`.length;
			const availableLabelWidth = 2 * LABEL_RADIUS * Math.sin(Math.min(sliceAngle, Math.PI) / 2);
			angle += sliceAngle;

			return {
				...slice,
				path: isFullCircle ? fullCirclePath() : roundedSlicePath(startAngle, endAngle),
				labelX,
				labelY,
				showLabel: availableLabelWidth >= labelLength * LABEL_CHARACTER_WIDTH,
				isFullCircle
			};
		});
	});

	readonly leadingRenderedSlice = computed<RenderedSlice | null>(() => {
		const leading = this.leadingSlice();
		if (!leading) {
			return null;
		}

		const rendered = this.renderedSlices().find(slice => slice.name === leading.name);
		return rendered ?? null;
	});
}
